import { AutocompleteInputChangeReason, debounce } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { workLocationsAutoCompleteQuery } from "../../../../../features/customer/customerAPI";
import { WorkLocation as WorkLocationExtended } from "../../../../../features/customer/types/queries";
import { Nullable } from "../../../../types/nullable";

const useCustomerOrgsAndWorkLocationsAutocomplete = (
  includeInactive: boolean = false,
) => {
  const [workLocation, setWorkLocation] =
    useState<Nullable<WorkLocationExtended>>(null);
  const [workLocationSearch, setWorkLocationSearch] = useState("");
  const [workLocationOptions, setWorkLocationOptions] = useState<
    WorkLocationExtended[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const onChange = useCallback((newValue: Nullable<WorkLocationExtended>) => {
    setWorkLocation(newValue);
  }, []);

  const onInputChangeCallback = useCallback(
    (value: string, reason: AutocompleteInputChangeReason) => {
      if (reason === "input") setWorkLocationSearch(value);
    },
    [],
  );

  const debouncedOnInputChange = useMemo(
    () => debounce(onInputChangeCallback, 400),
    [onInputChangeCallback],
  );

  const dispatchWorkLocationSearch = useCallback((searchString: string) => {
    setIsLoading(true);
    workLocationsAutoCompleteQuery({ term: searchString })
      .then((res) => {
        let filteredResults = res;

        if (!includeInactive) {
          filteredResults = filteredResults.filter((item) => item.active);
        }

        const sortedOptions = filteredResults.sort(
          (a, b) =>
            a.customerOrgName?.localeCompare(b.customerOrgName || "") ||
            0 ||
            a.name.localeCompare(b.name),
        );

        setWorkLocationOptions(sortedOptions);
      })
      .finally(() => {
        setIsLoading(false);
      });
      // FIXME: fix this or add comment why this is needed
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (workLocationSearch) {
      dispatchWorkLocationSearch(workLocationSearch);
    }
  }, [workLocationSearch, dispatchWorkLocationSearch]);

  const reset = useCallback(() => {
    setWorkLocation(null);
    setWorkLocationSearch("");
    setWorkLocationOptions([]);
  }, []);

  return {
    workLocation,
    workLocationOptions,
    onInputChange: debouncedOnInputChange,
    onChange,
    isLoading,
    reset,
  };
};

export default useCustomerOrgsAndWorkLocationsAutocomplete;
